import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Navbar, Footer } from '@/components/Layout';
import { XCircle, DoorOpen, ArrowRight, ExternalLink, Lock } from 'lucide-react';
import CallbackDemoHero from '@/pages/for-issuers/CallbackDemoHero';

const WRONG_DOOR = [
  { label: 'The processor', body: 'Paid on approvals. Adds a check only when a network rule mandates it.' },
  { label: 'The network', body: 'Does not have a rule for agent mandates today, and none is scheduled.' },
  { label: 'The standards body', body: 'Working group meetings are not a deployment pathway.' },
  { label: 'The scheme', body: 'Every rule change is a multi-year negotiation across five continents.' },
];

const OPEN_DOOR = [
  { label: 'The issuer authorization callback', body: 'Every card an agent holds was issued by someone. At authorization, the issuer already asks the program owner: approve or decline. Two-second window. Existing infrastructure.' },
  { label: 'The policy-check function inside that callback', body: 'The recourse layer\u2019s real home. Ten-line composition. Same shape as an existing 3DS callback.' },
  { label: 'The BIN sponsor / issuing program', body: 'Marqeta-shaped, Highnote-shaped, Lithic-shaped. Program-owner conversation, not a network conversation.' },
  { label: 'The unmandated transaction becomes an authorization that never happened', body: 'Not a violation to detect afterward. A refusal before the network ever routes.' },
];

const CALLBACK_CODE = `# 10-line composition inside an existing issuer authorization callback
from kojiru_recourse_blueprint.kypa_client import KYPAClient

kypa = KYPAClient()  # reads KOJIRU_API_URL from env

async def on_authorization(auth_request):
    credential = auth_request.metadata.get("kypa_credential")
    verdict = await kypa.verify(credential, intent={
        "agent_id": auth_request.cardholder_id,
        "recipient": auth_request.merchant_id,
        "amount_cents": auth_request.amount_cents,
        "category": auth_request.mcc_category,
    })
    if verdict.refused:
        return "decline", verdict.reason   # never authorized
    return "approve", None                  # forward to the network`;

export default function ForIssuers() {
  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="for-issuers-page">
      <Helmet>
        <title>For Issuing Programs: The Recourse Layer Composes Into Your Authorization Callback</title>
        <meta name="description" content="Every card an agent holds was issued by someone. At authorization, the issuer already calls the program owner in a two-second window. Kojiru's KYPA verify is the policy-check function that composes into that callback in ten lines. No processor cooperation. No network rule. No standards body." />
        <link rel="alternate" type="application/ld+json" href="/kojiru-schema.jsonld" />
        <link rel="alternate" type="application/json" href="/.well-known/kypa" title="KYPA Protocol Manifest" />
      </Helmet>

      <Navbar />

      {/* HERO */}
      <section className="pt-24 pb-10 px-6 border-b border-white/10">
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="text-[10px] font-bold tracking-[2.4px] text-amber-400 mb-4" data-testid="hero-eyebrow">
              FOR ISSUING PROGRAMS &middot; TODAY&rsquo;S DISPUTES, TOMORROW&rsquo;S AGENTS
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] mb-6 tracking-tight" data-testid="hero-headline">
              The processor will not check.<br />
              <span className="text-amber-400">The card already does.</span>
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed max-w-3xl" data-testid="hero-body">
              US retail returns hit <strong className="text-white">$890 billion in 2024</strong>, 17% of merchandise sales, and no category leader has emerged (a venture firm, September 2026). The dispute-evidence packet issuers receive today has not materially changed since 2015. Every card an agent holds was issued by someone, and at authorization the issuer already calls the program owner in a two-second window: approve or decline. Put the KYPA policy check inside that call and an out-of-scope transaction is not a violation you detect afterward: it is an <strong className="text-white">authorization that never happened</strong>. The same credential drops directly into VMPI, RDR, a card-network dispute service, and a major card network CE 3.0 as compelling evidence for today&rsquo;s browser-clicked disputes. No processor cooperation. No network rule. No standards body. <strong className="text-white">Live for today. Ready for the agentic wave when it arrives.</strong>
            </p>
          </motion.div>
        </div>
      </section>

      {/* KYA vs KYPA: ISSUER BALANCE-SHEET-RISK ANGLE */}
      <section className="py-16 px-6 border-b border-white/10" data-testid="issuer-kya-vs-kypa-section">
        <div className="max-w-5xl mx-auto">
          <div className="mb-8">
            <div className="text-[10px] font-bold tracking-[2.4px] text-amber-400 mb-3" data-testid="issuer-kya-vs-kypa-eyebrow">
              KYA VS KYPA &middot; ISSUER BALANCE-SHEET ANGLE
            </div>
            <h2 className="text-3xl sm:text-4xl font-black text-white leading-tight mb-4" data-testid="issuer-kya-vs-kypa-heading">
              KYA identity is necessary. <span className="text-amber-400">KYPA collateral is what protects your book.</span>
            </h2>
            <p className="text-slate-300 leading-relaxed max-w-3xl" data-testid="issuer-kya-vs-kypa-lede">
              The industry Know-Your-Agent (KYA) framework proposed by two major card schemes and a global wallet in September 2026 answers <em className="not-italic text-white">who the agent is.</em> It does not answer <em className="not-italic text-white">who pays when the agent goes off-script.</em> KYA identity does not shift downstream exposure: it only makes the identity legible. KYPA does. Because the agent operator posts pre-pledged collateral on-chain before authorization, the issuer can see who stands behind the agent before it authorizes. The pledge is a public commitment signal, not insurance and not a promise of repayment.
            </p>
          </div>

          <div className="rounded-2xl border border-white/10 overflow-x-auto bg-slate-900/40" data-testid="issuer-kya-vs-kypa-table">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-white/5">
                <tr>
                  <th className="text-left px-5 py-3 text-[10px] uppercase tracking-widest text-slate-400 font-bold w-2/5">Question the credential answers</th>
                  <th className="text-left px-5 py-3 text-[10px] uppercase tracking-widest text-slate-400 font-bold">Industry KYA framework</th>
                  <th className="text-left px-5 py-3 text-[10px] uppercase tracking-widest text-amber-300 font-bold">KYPA &middot; Kojiru</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-white/5" data-testid="issuer-row-question">
                  <td className="px-5 py-3 font-semibold text-white">Question resolved at authorization</td>
                  <td className="px-5 py-3 text-slate-400">Who the agent is</td>
                  <td className="px-5 py-3 text-emerald-300 font-semibold">Who pays when the agent goes off-script</td>
                </tr>
                <tr className="border-t border-white/5 bg-white/[0.01]" data-testid="issuer-row-exposure">
                  <td className="px-5 py-3 font-semibold text-white">Balance-sheet exposure</td>
                  <td className="px-5 py-3 text-slate-400">Issuer absorbs downstream: chargeback reserve, forensic dispute cost, brand cost</td>
                  <td className="px-5 py-3 text-emerald-300"><strong>Issuer stays out of the liability path</strong></td>
                </tr>
                <tr className="border-t border-white/5" data-testid="issuer-row-recovery">
                  <td className="px-5 py-3 font-semibold text-white">Recovery mechanism</td>
                  <td className="px-5 py-3 text-slate-500 italic">Not specified: falls back to scheme adjudication, days-to-weeks</td>
                  <td className="px-5 py-3 text-emerald-300"><strong>Signed mandate, public pledge, and on-chain evidence for existing processes</strong></td>
                </tr>
                <tr className="border-t border-white/5 bg-white/[0.01]" data-testid="issuer-row-loss-layer">
                  <td className="px-5 py-3 font-semibold text-white">Loss-absorption layer</td>
                  <td className="px-5 py-3 text-slate-400">Issuer book</td>
                  <td className="px-5 py-3 text-emerald-300">Agent-operator pre-pledged collateral pool</td>
                </tr>
                <tr className="border-t border-white/5" data-testid="issuer-row-underwriting">
                  <td className="px-5 py-3 font-semibold text-white">Underwriting evidence for software cardholders</td>
                  <td className="px-5 py-3 text-slate-400">Identity assertion</td>
                  <td className="px-5 py-3 text-emerald-300">Cryptographic assertion + on-chain collateral commitment</td>
                </tr>
                <tr className="border-t border-white/5 bg-white/[0.01]" data-testid="issuer-row-record">
                  <td className="px-5 py-3 font-semibold text-white">Dispute-record surface</td>
                  <td className="px-5 py-3 text-slate-500 italic">Scheme-adjudicated: party-written record</td>
                  <td className="px-5 py-3 text-emerald-300">Non-party on-chain evidence packet: a record no participant wrote for their own benefit</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-8 rounded-2xl bg-gradient-to-br from-amber-500/10 to-amber-500/[0.02] border border-amber-500/30 p-6" data-testid="issuer-kya-vs-kypa-callout">
            <p className="text-slate-200 leading-relaxed">
              If an issuer authorizes a transaction on a KYA-only credential and the agent acts outside its scope, the issuer&rsquo;s balance sheet still absorbs the risk downstream of authorization: through chargeback exposure, forensic dispute reserves, or brand-reputation cost. Under KYPA, the evidence is signed and public. The record is non-party. The issuer&rsquo;s book is not the loss-absorption layer.
            </p>
            <p className="text-amber-300 font-bold mt-4 leading-relaxed text-center">
              KYA gets you identity. KYPA gets you identity plus collateral. Pledged is the difference between an assertion and a commitment.
            </p>
          </div>
        </div>
      </section>

      {/* HERO VIDEO: self-playing callback refusal demo */}
      <section className="py-10 px-6 border-b border-white/10">
        <div className="max-w-6xl mx-auto">
          <div className="text-[10px] font-bold tracking-[2.4px] text-cyan-300 mb-3">
            LIVE CALLBACK DEMO &middot; $200 HOTEL &rarr; AGENT PRESENTS $600
          </div>
          <h2 className="text-2xl sm:text-3xl font-black text-white mb-5 leading-tight">
            Watch the refusal happen inside the two-second window.
          </h2>
          <CallbackDemoHero />
          <p className="text-xs text-slate-500 mt-4 italic max-w-3xl">
            Real code path. The same <code className="text-cyan-300">examples/issuer_callback_refusal.py</code> ships in the <code className="text-cyan-300">kojiru-recourse-blueprint</code> repository, and pytest verifies the exact refusal shape (<code className="text-cyan-300">(&ldquo;decline&rdquo;, &ldquo;mandate_scope_exceeded&rdquo;)</code>) on every commit. Prefer a real terminal recording? Download the <a href="/downloads/for-issuers-callback-demo.cast?v=13" className="text-cyan-300 hover:underline">asciinema cast</a> and play it locally with <code className="text-cyan-300">asciinema play</code> or upload to asciinema.org.
          </p>
        </div>
      </section>

      {/* WRONG DOOR / OPEN DOOR */}
      <section className="py-14 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="border border-slate-500/20 rounded-2xl p-6 bg-slate-500/[0.04]" data-testid="wrong-door-column">
              <div className="flex items-center gap-3 mb-5">
                <XCircle className="w-6 h-6 text-slate-400" />
                <div className="text-[10px] font-bold tracking-[1.6px] text-slate-400">WRONG DOOR</div>
              </div>
              <ul className="space-y-4">
                {WRONG_DOOR.map((r) => (
                  <li key={r.label}>
                    <div className="text-slate-200 font-bold text-sm mb-1">{r.label}</div>
                    <div className="text-sm text-slate-400 leading-relaxed">{r.body}</div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="border border-amber-500/30 rounded-2xl p-6 bg-amber-500/[0.05]" data-testid="open-door-column">
              <div className="flex items-center gap-3 mb-5">
                <DoorOpen className="w-6 h-6 text-amber-400" />
                <div className="text-[10px] font-bold tracking-[1.6px] text-amber-400">OPEN DOOR</div>
              </div>
              <ul className="space-y-4">
                {OPEN_DOOR.map((r) => (
                  <li key={r.label}>
                    <div className="text-white font-bold text-sm mb-1">{r.label}</div>
                    <div className="text-sm text-slate-300 leading-relaxed">{r.body}</div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-6 text-center italic" data-testid="attribution-note">
            Frame adapted from a senior payments executive publicly writing on issuing-program design, September 2026.
          </p>
        </div>
      </section>

      {/* SEQUENCE STRIP */}
      <section className="py-14 px-6 bg-slate-900/50 border-y border-white/10">
        <div className="max-w-5xl mx-auto">
          <div className="text-xs font-bold tracking-[2px] text-cyan-300 mb-3">THE TWO-SECOND WINDOW</div>
          <h2 className="text-2xl sm:text-3xl font-black text-white mb-6 leading-tight">
            Agent presents card &rarr; issuer authorization &rarr; <span className="text-amber-400">policy check</span> &rarr; approve or decline
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="sequence-strip">
            <div className="border border-white/10 rounded-lg p-3 text-center bg-slate-800/40">
              <div className="text-[10px] font-bold text-slate-400 tracking-widest mb-1">STEP 1</div>
              <div className="text-white font-bold text-sm">Agent presents card</div>
            </div>
            <div className="border border-white/10 rounded-lg p-3 text-center bg-slate-800/40">
              <div className="text-[10px] font-bold text-slate-400 tracking-widest mb-1">STEP 2</div>
              <div className="text-white font-bold text-sm">Issuer authorization</div>
            </div>
            <div className="border-2 border-amber-500 rounded-lg p-3 text-center bg-amber-500/10 relative">
              <div className="text-[10px] font-bold text-amber-400 tracking-widest mb-1">STEP 3 &middot; THE LOCK</div>
              <div className="text-white font-bold text-sm">Policy check</div>
              <div className="text-[10px] text-amber-300 mt-1">issuer callback &middot; 2s</div>
            </div>
            <div className="border border-white/10 rounded-lg p-3 text-center bg-slate-800/40">
              <div className="text-[10px] font-bold text-slate-400 tracking-widest mb-1">STEP 4</div>
              <div className="text-white font-bold text-sm">Approve or decline</div>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4 mt-6">
            <div className="border border-emerald-500/25 rounded-xl p-4 bg-emerald-500/5">
              <div className="text-xs font-bold text-emerald-400 mb-2">MANDATE VALID</div>
              <p className="text-sm text-slate-300 leading-relaxed">The authorization completes and the record was written before anyone asked. Zero merchant-side change.</p>
            </div>
            <div className="border border-red-500/25 rounded-xl p-4 bg-red-500/5">
              <div className="text-xs font-bold text-red-400 mb-2">NO MANDATE</div>
              <p className="text-sm text-slate-300 leading-relaxed">Not a violation to detect afterward. An authorization that never happened. No chargeback risk. No dispute-rate exposure.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CODE COMPOSITION */}
      <section className="py-14 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-xs font-bold tracking-[2px] text-cyan-300 mb-3">THE COMPOSITION</div>
          <h2 className="text-2xl sm:text-3xl font-black text-white mb-5 leading-tight">
            Ten lines inside the callback you already run.
          </h2>
          <pre className="bg-slate-900 border border-white/10 rounded-xl p-5 overflow-x-auto text-xs text-slate-200 leading-relaxed" data-testid="issuer-callback-composition">
            <code>{CALLBACK_CODE}</code>
          </pre>
          <p className="text-sm text-slate-400 leading-relaxed mt-4">
            Same primitive as the merchant-edge composition: just wired into the issuer authorization callback instead of the merchant checkout webhook. Reference implementation lives at <code className="text-cyan-300 text-xs">kojiru-recourse-blueprint / examples / issuer_callback_refusal.py</code>. Pytest passes on the canonical $200-hotel-booked-at-$600 refusal, same taxonomy as the merchant blueprint.
          </p>
        </div>
      </section>

      {/* WHAT THIS BUYS THE PROGRAM */}
      <section className="py-14 px-6 bg-slate-900/60 border-y border-white/10">
        <div className="max-w-5xl mx-auto">
          <div className="text-xs font-bold tracking-[2px] text-cyan-300 mb-3">WHAT VERIFYING KYPA BUYS YOUR PROGRAM</div>
          <h2 className="text-2xl sm:text-3xl font-black text-white mb-8 leading-tight">Three properties. One callback. Zero network dependencies.</h2>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="border border-white/10 rounded-xl p-5 bg-slate-950/60">
              <Lock className="w-5 h-5 text-amber-400 mb-3" />
              <div className="text-lg font-bold text-white mb-2">Refuse at authorization</div>
              <p className="text-sm text-slate-400 leading-relaxed">Out-of-scope agent transactions become declines inside your existing two-second callback. The network never sees them. Not a chargeback later; a decline now.</p>
            </div>
            <div className="border border-white/10 rounded-xl p-5 bg-slate-950/60">
              <Lock className="w-5 h-5 text-amber-400 mb-3" />
              <div className="text-lg font-bold text-white mb-2">Ship in a week</div>
              <p className="text-sm text-slate-400 leading-relaxed">No processor cooperation. No network rule. No standards body. One HTTP call from a code path you already own. The rest is program-side policy.</p>
            </div>
            <div className="border border-white/10 rounded-xl p-5 bg-slate-950/60">
              <Lock className="w-5 h-5 text-amber-400 mb-3" />
              <div className="text-lg font-bold text-white mb-2">Underwrite software cardholders</div>
              <p className="text-sm text-slate-400 leading-relaxed">The KYPA envelope is the first cryptographically verifiable answer to <em>"which cardholder is authorized to spend what?"</em> for software cardholders. Underwriting has evidence, not a chat log.</p>
            </div>
          </div>
        </div>
      </section>

      {/* THE MATH: issuer economics */}
      <section className="py-14 px-6 border-y border-white/10" data-testid="issuer-math-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-xs font-bold tracking-[2px] text-emerald-300 mb-3">THE MATH · WHAT THE CALLBACK BUYS THE PROGRAM</div>
          <h2 className="text-2xl sm:text-3xl font-black text-white mb-6 leading-tight">Deterministic liability. Priced in dollars.</h2>
          <p className="text-slate-300 leading-relaxed mb-6 max-w-3xl">
            First-principles math for a BIN sponsor's issuing program at 1&nbsp;million cardholders on agent-mediated traffic. Prevention is <strong className="text-white">roughly 2&times; cheaper</strong> than dispute recovery. No pledge recovery is assumed.
          </p>
          <div className="overflow-x-auto border border-white/10 rounded-xl">
            <table className="w-full text-sm">
              <tbody className="divide-y divide-white/5">
                <tr><td className="p-3 text-slate-400 w-2/3">Cardholders</td><td className="p-3 text-white font-mono text-right">1,000,000</td></tr>
                <tr><td className="p-3 text-slate-400">Activation rate &times; actions/yr</td><td className="p-3 text-white font-mono text-right">25% &times; 12</td></tr>
                <tr><td className="p-3 text-slate-400">Out-of-scope error rate</td><td className="p-3 text-white font-mono text-right">0.1%</td></tr>
                <tr className="bg-red-500/[0.05]"><td className="p-3 text-red-200 font-bold">Annual dispute exceptions</td><td className="p-3 text-red-200 font-mono text-right font-bold">3,000</td></tr>
                <tr className="bg-red-500/[0.05]"><td className="p-3 text-red-200 font-bold">Annual ops margin destroyed</td><td className="p-3 text-red-200 font-mono text-right font-bold">$75K–$150K</td></tr>
                <tr className="bg-emerald-500/[0.05]"><td className="p-3 text-emerald-200 font-bold">Annual KYPA verify spend ($0.10 &times; 3M calls)</td><td className="p-3 text-emerald-200 font-mono text-right font-bold">$300K</td></tr>
                <tr className="bg-emerald-500/[0.08]"><td className="p-3 text-white font-bold">Program-level annual benefit envelope</td><td className="p-3 text-emerald-300 font-mono text-right font-bold">$28M–$84M</td></tr>
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-500 leading-relaxed mt-4 italic max-w-3xl">
            The $28M–$84M annual-benefit envelope reflects the full interface-and-economics governance a program captures when the callback owns the mandate verification: well documented in the payments-industry literature on agent-directory economics. Kojiru does not charge a percentage of that envelope; the callback fee is flat per verify.
          </p>
        </div>
      </section>

      {/* TWO-PHASE AUTONOMY */}
      <section className="py-14 px-6 bg-slate-900/60 border-b border-white/10" data-testid="two-phase-autonomy-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-xs font-bold tracking-[2px] text-cyan-300 mb-3">TWO-PHASE AUTONOMY</div>
          <h2 className="text-2xl sm:text-3xl font-black text-white mb-4 leading-tight">
            Bounded autonomy today. Full autonomy when the callback proves it.
          </h2>
          <p className="text-slate-300 leading-relaxed mb-8 max-w-3xl">
            A BIN sponsor launching agent cards for the first time does not have to jump straight to 3&nbsp;AM full autonomy. The KYPA credential envelope exposes an <strong className="text-white">optional</strong> Review-and-Release flag: agents above a threshold amount get routed to a biometric re-auth before the transaction authorizes. The default state of every credential is fully autonomous. The flag is a phase-one dial you turn off once your book is proven.
          </p>
          <div className="grid md:grid-cols-2 gap-5">
            <div className="border border-cyan-500/25 rounded-xl p-5 bg-cyan-500/[0.04]" data-testid="phase-one-card">
              <div className="text-[10px] font-bold tracking-widest text-cyan-300 mb-3">PHASE 1 · LAUNCH BOOK</div>
              <div className="text-xl font-bold text-white mb-3">Review &amp; Release</div>
              <ul className="text-sm text-slate-300 leading-relaxed space-y-2 mb-3">
                <li>&bull; Credential envelope carries <code className="text-cyan-300 text-xs">human_review_required: true</code></li>
                <li>&bull; Every transaction above <code className="text-cyan-300 text-xs">review_threshold_cents</code> routes to biometric re-auth</li>
                <li>&bull; Below the threshold, the callback approves autonomously in &lt;100&thinsp;ms</li>
                <li>&bull; Reference example: <code className="text-cyan-300 text-xs">examples/review_release_refusal.py</code></li>
              </ul>
              <p className="text-xs text-slate-500 italic">
                Fits the first six months of a launch book. Underwriters get a bounded exposure; cardholders get a familiar re-auth pattern at high value.
              </p>
            </div>
            <div className="border-2 border-emerald-500 rounded-xl p-5 bg-emerald-500/[0.05]" data-testid="phase-two-card">
              <div className="text-[10px] font-bold tracking-widest text-emerald-300 mb-3">PHASE 2 · STEADY STATE · THE ENDGAME</div>
              <div className="text-xl font-bold text-white mb-3">Fully Autonomous · 3&nbsp;AM Default</div>
              <ul className="text-sm text-slate-300 leading-relaxed space-y-2 mb-3">
                <li>&bull; Credential envelope omits <code className="text-emerald-300 text-xs">review_config</code> entirely</li>
                <li>&bull; Every transaction verifies against the pledge in a single sub-100&thinsp;ms callback</li>
                <li>&bull; Zero humans in the loop. Zero re-auth prompts. Zero ops labor per transaction.</li>
                <li>&bull; The slashable pledge is the deterministic backstop: capital scales with agent trust history (ACS™)</li>
              </ul>
              <p className="text-xs text-slate-500 italic">
                Humans sleep; the pledge does not. This is the operating mode agentic commerce eventually converges on for the whole book.
              </p>
            </div>
          </div>
          <div className="mt-6 border border-white/10 rounded-xl p-4 bg-black/40">
            <div className="text-[10px] font-bold tracking-widest text-slate-400 mb-2">THE UPGRADE PATH</div>
            <p className="text-sm text-slate-300 leading-relaxed">
              Same primitive, same callback, same code. Phase&nbsp;1 &rarr; Phase&nbsp;2 is a one-line credential-schema change on the issuer side. No re-deploy. No re-integration. The Review-and-Release flag exists so nervous programs can launch; the pledge exists so every program can eventually run at 3&nbsp;AM.
            </p>
          </div>
        </div>
      </section>

      {/* THREE-AUDIENCE TRIANGLE */}
      <section className="py-14 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-xs font-bold tracking-[2px] text-slate-500 mb-3">THE THREE-AUDIENCE TRIANGLE</div>
          <h2 className="text-2xl sm:text-3xl font-black text-white mb-6 leading-tight">Same primitive. Three composition surfaces.</h2>
          <p className="text-slate-300 leading-relaxed mb-6 max-w-3xl">
            Kojiru\u2019s recourse plane composes into three different code paths, depending on which role reads first. The strongest coverage runs all three; the fastest single-surface deployment is the issuer callback.
          </p>
          <div className="grid md:grid-cols-3 gap-4">
            <Link to="/for-issuers" className="border-2 border-amber-500 rounded-xl p-5 bg-amber-500/[0.06]" data-testid="triangle-issuers">
              <div className="text-[10px] font-bold text-amber-400 tracking-widest mb-2">YOU ARE HERE</div>
              <div className="text-white font-bold mb-1">Issuing programs</div>
              <div className="text-xs text-slate-400">Callback-side verify. Two-second window. Zero network dependencies.</div>
            </Link>
            <Link to="/for-merchants" className="border border-white/10 rounded-xl p-5 bg-slate-900/40 hover:border-white/20 transition-colors" data-testid="triangle-merchants">
              <div className="text-[10px] font-bold text-slate-500 tracking-widest mb-2">MERCHANTS</div>
              <div className="text-white font-bold mb-1">Merchant checkout webhook</div>
              <div className="text-xs text-slate-400">Edge-side verify. Refuse out-of-mandate intents before settlement.</div>
              <ArrowRight className="w-4 h-4 text-slate-500 mt-3" />
            </Link>
            <Link to="/for-banks" className="border border-white/10 rounded-xl p-5 bg-slate-900/40 hover:border-white/20 transition-colors" data-testid="triangle-banks">
              <div className="text-[10px] font-bold text-slate-500 tracking-widest mb-2">BANKS</div>
              <div className="text-white font-bold mb-1">Bank product / risk</div>
              <div className="text-xs text-slate-400">Retention thesis. A consumer research firm trust parity. Recourse as a bank differentiator.</div>
              <ArrowRight className="w-4 h-4 text-slate-500 mt-3" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 px-6 bg-slate-900/70 border-t border-white/10">
        <div className="max-w-4xl mx-auto">
          <div className="text-xs font-bold tracking-[2px] text-slate-500 mb-4">THE PROGRAM IS THE WORK</div>
          <h2 className="text-2xl sm:text-3xl font-black text-white mb-5 leading-tight">
            Which issuing program will put its name on a cardholder that is software, and on what terms?
          </h2>
          <p className="text-slate-300 leading-relaxed mb-4">
            The wiring is a week. The program (underwriting policy, KYC-for-software, cardholder-agreement text, category-restriction ladders, initial pool sizing) is the work. If you sponsor a BIN and are being asked to issue a card to an agent, Kojiru is the counterparty you compose the callback against.
          </p>
          <p className="text-slate-300 leading-relaxed mb-6">
            Full protocol spec at <Link to="/kypa/instrument" className="text-cyan-300 hover:underline">/kypa/instrument</Link>. First live on-chain slash proof at <Link to="/proofs/slash-sepolia-000001.json" className="text-cyan-300 hover:underline">/proofs/slash-sepolia-000001.json</Link>. Reference blueprint (merchant + issuer surfaces) at <a href="https://github.com" className="text-cyan-300 hover:underline">kojiru-recourse-blueprint</a>.
          </p>
          <div className="flex flex-wrap gap-3">
            <a href="/downloads/kypa-facilitator-integration-brief.pdf?v=12" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-bold text-emerald-300 hover:text-emerald-200 border border-emerald-500/30 rounded-md px-4 py-2" data-testid="brief-download">
              KYPA integration brief <ExternalLink className="w-3 h-3" />
            </a>
            <a href="/downloads/kojiru-outreach-issuing-programs.pdf?v=12" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-bold text-amber-300 hover:text-amber-200 border border-amber-500/30 rounded-md px-4 py-2" data-testid="outreach-download">
              Issuing-program pitch (PDF) <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
